import { Button, Checkbox, Flex, Input, Text, Tooltip } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { CloseOutlined } from "@/icons"
import { getDestinationLabel } from "@/pages/GovernancePage/applications/ai-policies/liveRuleGroups.selector"
import {
  getSubdomainsChoice,
  isNeverSubdomains
} from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.selector"

import styles from "./RuleField.module.scss"

import type { DestinationInputProps } from "./RuleField.types"

// One destination: a host name, with its subdomains when checked, an IP
// address or an address block, and the ports it takes once added. A preset
// loaded with the policy only shows. The subdomains choice holds while the
// text is being typed, and goes when the input is left naming an address or
// a *. name, so a host name that starts like an address keeps it. The
// checkbox always shows; its tooltip says why it cannot be checked, and a
// host name too long for its *. name shows the error while checked
export const DestinationInput = (props: DestinationInputProps) => {
  const { value = {}, onChange } = props
  const { t } = useTranslation()
  const tKey = "governance.ai.live.form.destinations"

  if (value.kept)
    return (
      <Text className={styles.label}>{getDestinationLabel(t, value.kept)}</Text>
    )

  const { ports, ...destination } = value
  const { hostname, reason, disabled } = getSubdomainsChoice(value)

  return (
    <Flex gap={8} align="center">
      <Input
        className={styles.f1}
        value={value.value}
        placeholder={t(`${tKey}.placeholders.destination`)}
        onChange={(event) =>
          onChange?.({ ...value, value: event.target.value })
        }
        onBlur={() => {
          if (value.subdomains && isNeverSubdomains(value.value))
            onChange?.({ ...value, subdomains: false })
        }}
      />
      <Tooltip title={t(`${tKey}.${reason}`, { hostname })}>
        <Checkbox
          className={styles.nowrap}
          checked={!!value.subdomains}
          disabled={disabled}
          onChange={(event) =>
            onChange?.({ ...value, subdomains: event.target.checked })
          }
        >
          {t(`${tKey}.subdomains`)}
        </Checkbox>
      </Tooltip>
      {ports === undefined ? (
        <Button
          type="link"
          size="small"
          className={styles.buttonP0}
          onClick={() => onChange?.({ ...value, ports: "" })}
        >
          {t(`${tKey}.addPort`)}
        </Button>
      ) : (
        <>
          <Input
            className={styles.ports}
            value={ports}
            placeholder={t(`${tKey}.placeholders.ports`)}
            onChange={(event) =>
              onChange?.({ ...value, ports: event.target.value })
            }
          />
          <Button
            type="text"
            size="small"
            aria-label={t(`${tKey}.removePort`)}
            icon={
              <Text type="secondary" className="sm-text-ks">
                <CloseOutlined />
              </Text>
            }
            onClick={() => onChange?.(destination)}
          />
        </>
      )}
    </Flex>
  )
}
